'use client';

import React from 'react';
import { styled } from 'styled-components';
import cs from 'classnames';
import Link from 'next/link';
import * as Styled from '@lib/styles';
import { trackLink } from '@lib/tracking';
import { legacyImageDisplayWidth } from '@lib/legacy-image-display';
import type { CardColor, SafeUrl } from '@types';
import ContentfulImage from './ContentfulImage';

export type QuoteCardProps = {
  /**
   * Color scheme of the card
   */
  colorScheme?: CardColor | 'transparent';
  /**
   * The author of the quote on the card
   */
  attribution: string;

  /**
   * The title of the attributing author
   */
  attributionTitle: string;
  /**
   * The card quote
   */
  quote: string;
  /**
   * Optional class name, nesc. for extending as a styled-component
   */
  className?: string;
  /**
   * URL of image
   */
  imageUrl: SafeUrl;

  /**
   * Alt text for the logo being displayed
   */
  imageAltText: string;

  /**
   * Width of image being displayed
   */
  imageWidth: number;

  /**
   *  Height of image being displayed
   */
  imageHeight: number;

  /**
   * Optional URL for the attributed company. When set, the attribution title
   * renders as a link; when omitted, it renders as plain text.
   */
  companyUrl?: SafeUrl;

  /**
   * Tracking label for the company link, e.g. 'highline-studios'. Only used
   * when companyUrl is set.
   */
  companyTrackingLabel?: string;

  /**
   * Tracking property for the company link, narrowing where on the page it
   * sits, e.g. 'player-customer-story'. Only used when companyUrl is set.
   */
  companyTrackingProperty?: string;
};

const QuoteCardContainer = styled.blockquote`
  border-radius: ${({ theme }) => theme.radius.std};
  display: grid;
  gap: ${({ theme }) => theme.spacing['1']};
  grid-template-rows: 1fr min-content min-content;
  height: 100%;
  line-height: ${({ theme }) => theme.typography.lineHeight.body};
  margin: 0;
  padding: ${({ theme }) => theme.spacing['2']};

  &.yellow {
    background-color: ${({ theme }) => theme.colors.yellow200};
  }

  &.orange {
    background-color: ${({ theme }) => theme.colors.orange200};
  }

  &.pink {
    background-color: ${({ theme }) => theme.colors.pink300};
  }

  &.purple {
    background-color: ${({ theme }) => theme.colors.purple300};
  }

  &.green {
    background-color: ${({ theme }) => theme.colors.green300};
  }

  &.lightBlue {
    background-color: ${({ theme }) => theme.colors.blue200};
  }
  /* The blue color scheme is identical to lightBlue, but I didn't want to touch the type definition for CardColor (yet). */
  &.blue {
    background-color: ${({ theme }) => theme.colors.blue200};
  }

  &.darkBlue {
    background-color: ${({ theme }) => theme.colors.blue700};
    color: ${({ theme }) => theme.colors.white};
  }

  &.white {
    background-color: ${({ theme }) => theme.colors.white};
    color: ${({ theme }) => theme.colors.blue800};
  }

  &.transparent {
    background-color: transparent;
    color: inherit;
  }

  ${({ theme }) => theme.mq.minWidth.large} {
    padding: ${({ theme }) => theme.spacing['4']};
  }
`;

const QuoteImage = styled(ContentfulImage)`
  border-radius: unset;
  height: auto;
  max-width: 100%;
  width: ${({ width }) => legacyImageDisplayWidth(width)}px;

  @media (resolution > 1dppx) {
    width: ${({ width }) => legacyImageDisplayWidth(width, 2)}px;
  }
`;

const QuoteWrapper = styled(Styled.BodyLg)`
  margin: 0;
  padding-top: ${({ theme }) => theme.spacing['6']};
`;

const AuthorText = styled.figcaption`
  display: flex;
  flex-direction: column;
  font-style: normal;
  font-weight: bold;

  ${({ theme }) => theme.mq.minWidth.large} {
    flex-direction: row;
  }
`;

const AuthorTitle = styled.span`
  font-weight: normal;
  margin-left: 0;

  ${({ theme }) => theme.mq.minWidth.large} {
    margin-left: ${({ theme }) => theme.spacing['1.5']};
  }
`;

/**
 * Inherits the card's foreground color so it reads correctly against every
 * color scheme, including darkBlue and white.
 */
const CompanyLink = styled(Link)`
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 0.15em;
  transition: text-decoration-thickness var(--global-motion);

  &:hover,
  &:focus-visible {
    text-decoration-thickness: 2px;
  }
`;

const handleCompanyClick = (trackingLabel, trackingProperty) => {
  if (trackingLabel || trackingProperty) {
    trackLink(trackingLabel, trackingProperty);
  }
};

const QuoteCard = ({
  className,
  colorScheme = 'lightBlue',
  companyTrackingLabel,
  companyTrackingProperty,
  companyUrl,
  imageAltText,
  imageHeight,
  imageUrl,
  imageWidth,
  attribution,
  quote,
  attributionTitle,
}: QuoteCardProps) => {
  return (
    // The quoteCard classname is used to apply module specific styles; e.g. CustomerStory module.
    <QuoteCardContainer className={cs('quoteCard', colorScheme, className)}>
      <QuoteImage
        alt={imageAltText}
        height={imageHeight}
        src={imageUrl}
        width={imageWidth}
      />
      <QuoteWrapper>“{quote}”</QuoteWrapper>
      <AuthorText>
        {attribution}{' '}
        <AuthorTitle>
          {companyUrl ? (
            <CompanyLink
              href={companyUrl}
              onClick={() =>
                handleCompanyClick(
                  companyTrackingLabel,
                  companyTrackingProperty,
                )
              }
            >
              {attributionTitle}
            </CompanyLink>
          ) : (
            attributionTitle
          )}
        </AuthorTitle>
      </AuthorText>
    </QuoteCardContainer>
  );
};

export default QuoteCard;
